import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { 
  Product, 
  ProductsResponse, 
  ProductFilters, 
  PaginationParams,
  ProductVariant 
} from '../types';

// Query keys
export const productKeys = {
  all: ['products'] as const,
  lists: () => [...productKeys.all, 'list'] as const,
  list: (filters: ProductFilters & PaginationParams) => [...productKeys.lists(), filters] as const,
  details: () => [...productKeys.all, 'detail'] as const,
  detail: (id: string) => [...productKeys.details(), id] as const,
  lowStock: (storeId?: string) => [...productKeys.all, 'low-stock', storeId] as const,
};

// Fetch products with pagination and filters
export function useProducts(filters: ProductFilters & PaginationParams = {}) {
  const queryParams = new URLSearchParams();
  
  if (filters.storeId) queryParams.set('storeId', filters.storeId);
  if (filters.categoryId) queryParams.set('categoryId', filters.categoryId);
  if (filters.status) queryParams.set('status', filters.status);
  if (filters.type) queryParams.set('type', filters.type);
  if (filters.featured !== undefined) queryParams.set('featured', String(filters.featured));
  if (filters.lowStock !== undefined) queryParams.set('lowStock', String(filters.lowStock));
  if (filters.search) queryParams.set('search', filters.search);
  if (filters.minPrice !== undefined) queryParams.set('minPrice', String(filters.minPrice));
  if (filters.maxPrice !== undefined) queryParams.set('maxPrice', String(filters.maxPrice));
  if (filters.cursor) queryParams.set('cursor', filters.cursor);
  if (filters.limit) queryParams.set('limit', String(filters.limit));
  if (filters.sortBy) queryParams.set('sortBy', filters.sortBy);
  if (filters.sortOrder) queryParams.set('sortOrder', filters.sortOrder);

  const queryString = queryParams.toString();
  const url = `/products${queryString ? `?${queryString}` : ''}`;

  return useQuery({
    queryKey: productKeys.list(filters),
    queryFn: () => api.get<ProductsResponse>(url),
  });
}

// Infinite scroll products
export function useInfiniteProducts(filters: Omit<ProductFilters & PaginationParams, 'cursor'> = {}) {
  return useInfiniteQuery({
    queryKey: productKeys.list({ ...filters, cursor: undefined }),
    queryFn: ({ pageParam }) => {
      const queryParams = new URLSearchParams();
      
      if (filters.storeId) queryParams.set('storeId', filters.storeId);
      if (filters.categoryId) queryParams.set('categoryId', filters.categoryId);
      if (filters.status) queryParams.set('status', filters.status);
      if (filters.search) queryParams.set('search', filters.search);
      if (filters.limit) queryParams.set('limit', String(filters.limit));
      if (filters.sortBy) queryParams.set('sortBy', filters.sortBy);
      if (filters.sortOrder) queryParams.set('sortOrder', filters.sortOrder);
      if (pageParam) queryParams.set('cursor', pageParam);

      const queryString = queryParams.toString();
      return api.get<ProductsResponse>(`/products${queryString ? `?${queryString}` : ''}`);
    },
    getNextPageParam: (lastPage) => lastPage.pagination.nextCursor,
    initialPageParam: undefined as string | undefined,
  });
}

// Fetch single product
export function useProduct(id: string) {
  return useQuery({
    queryKey: productKeys.detail(id),
    queryFn: () => api.get<{ product: Product }>(`/products/${id}`),
    enabled: !!id,
  });
}

// Create product
export function useCreateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<Product>) => 
      api.post<{ product: Product }>('/products', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.lists() });
    },
  });
}

// Update product
export function useUpdateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Product> }) =>
      api.put<{ product: Product }>(`/products/${id}`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: productKeys.lists() });
      queryClient.invalidateQueries({ queryKey: productKeys.detail(variables.id) });
    },
  });
}

// Delete product
export function useDeleteProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/products/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.lists() });
    },
  });
}

// Duplicate product
export function useDuplicateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => 
      api.post<{ product: Product }>(`/products/${id}/duplicate`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.lists() });
    },
  });
}

// Update quantity
export function useUpdateQuantity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, quantity, operation }: { id: string; quantity: number; operation?: 'set' | 'increment' | 'decrement' }) =>
      api.patch<{ id: string; quantity: number }>(`/products/${id}/quantity`, { quantity, operation }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.lists() });
    },
  });
}

// Get low stock products
export function useLowStockProducts(storeId?: string) {
  return useQuery({
    queryKey: productKeys.lowStock(storeId),
    queryFn: () => {
      const url = storeId 
        ? `/products/inventory/low-stock?storeId=${storeId}`
        : '/products/inventory/low-stock';
      return api.get<{ products: Product[]; count: number }>(url);
    },
  });
}

// ============================================
// VARIANTS
// ============================================

export function useProductVariants(productId: string) {
  return useQuery({
    queryKey: [...productKeys.detail(productId), 'variants'],
    queryFn: () => api.get<{ variants: ProductVariant[] }>(`/products/${productId}/variants`),
    enabled: !!productId,
  });
}

export function useCreateVariant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, data }: { productId: string; data: Partial<ProductVariant> }) =>
      api.post<{ variant: ProductVariant }>(`/products/${productId}/variants`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: productKeys.detail(variables.productId) });
    },
  });
}

export function useUpdateVariant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, variantId, data }: { productId: string; variantId: string; data: Partial<ProductVariant> }) =>
      api.put<{ variant: ProductVariant }>(`/products/${productId}/variants/${variantId}`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: productKeys.detail(variables.productId) });
    },
  });
}

export function useDeleteVariant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, variantId }: { productId: string; variantId: string }) =>
      api.delete(`/products/${productId}/variants/${variantId}`),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: productKeys.detail(variables.productId) });
    },
  });
}
